import { CalendarClock } from 'lucide-react';

import { NumberStepper } from '@/components/sales/wizard/NumberStepper';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { inr } from '@/lib/sales-metrics';
import cn from '@/utils/cn';
import {
  calculateFlatProductTermTotal,
  clampMultiYearDiscountPercent,
} from '@/utils/productTermPricing';

interface MultiYearTermDiscountControlProps {
  /** Contract length in years. The control is only meaningful above 1. */
  termYears: number;
  /** Full-price annual rate for this line, before any discount. */
  annualPrice: number;
  /** Company-wide percentage, used when this line carries no override. */
  defaultPercent: number;
  /** The line's own override, or undefined when it follows the company setting. */
  percent: number | undefined;
  /** `null` clears the override and returns the line to the company setting. */
  onChange: (percent: number | null) => void;
  ariaLabel: string;
}

/**
 * Editable multi-year term discount for one quotation line.
 *
 * Year 1 is always charged in full and years 2+ come off this percentage, so a
 * 2-year line at 50% costs 1.5x the annual rate. That figure used to be
 * company-wide and fixed, which meant a negotiated term had to be faked through
 * the per-product discount and then read back as if it were one.
 *
 * Deliberately SEPARATE from ProductDiscountControl, and applied before it: this
 * one decides what the line's gross is, and the per-product discount then comes
 * off that gross. Collapsing the two would make the order they apply in
 * invisible, and the emailed quotation states them as different things.
 */
export function MultiYearTermDiscountControl({
  termYears,
  annualPrice,
  defaultPercent,
  percent,
  onChange,
  ariaLabel,
}: MultiYearTermDiscountControlProps) {
  const effective = percent ?? defaultPercent;
  const isOverridden = percent !== undefined;

  // What the term discount is worth on this line, in rupees: the difference
  // between paying the annual rate every year and the discounted schedule.
  const fullTerm = annualPrice * Math.max(1, termYears);
  const saved = Math.max(0, fullTerm - calculateFlatProductTermTotal(annualPrice, termYears, effective));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'mt-0.5 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold transition-colors',
            isOverridden
              ? 'border-primary/40 bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <CalendarClock className="h-3 w-3" />
          {`Y1 full + Y2–${termYears} at ${effective}% off`}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-3">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold text-foreground">Multi-year term discount</span>
            <span className="text-[11px] leading-snug text-muted-foreground">
              Year 1 is charged in full. This comes off each of years 2
              {termYears > 2 ? `–${termYears}` : ''}.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <NumberStepper
              value={effective}
              onChange={(v) => onChange(clampMultiYearDiscountPercent(v, defaultPercent))}
              max={100}
              step={5}
              inputWidthClassName="w-14"
              placeholder="0"
              ariaLabel="Multi-year discount percent"
            />
            <span className="text-xs text-muted-foreground">%</span>
          </div>
          {saved > 0 && (
            <div className="rounded-md bg-muted/60 px-2.5 py-1.5 text-xs text-ink/70">
              Saves <strong>{inr(saved)}</strong> over {termYears} years
            </div>
          )}
          {isOverridden && (
            <div className="flex items-center justify-between rounded-md bg-primary/5 px-2.5 py-1.5 text-[11px] text-ink/70">
              <span>{`Company default: ${defaultPercent}%`}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px] text-muted-foreground"
                onClick={() => onChange(null)}
              >
                Reset
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
